/**
 * Timestamp detection utilities (ported from den-core/packages/utilities/timestampUtils.ts)
 * Used to auto-detect if numeric values are likely Unix epoch timestamps.
 */

// Date the EVM was launched - July 30, 2015 - in unix epoch milliseconds
const ethereumLaunchDate = 1438282197000;
// Duration of three years in unix epoch milliseconds
const threeYears = 94608000000; // YEAR_MS * 3 where YEAR_MS = 31536000000

// Param names that contain these strings should NOT be timestamps
const blacklist = ['value', 'amount', 'uint']; // none of the time-related names included "value"

// Param names that contain these strings could be timestamps
const whitelist = [
  'deadline',
  'eta',
  'time',
  'date',
  'epoch',
  'after', // I haven't seen "before" before
  'until',
  'period',
  'expir', // I've seen "expiration" and "expiries"
  'start',
  'end',
  'conclusion',
  'term',
];

// Param names paired with these method names could be timestamps
interface WhitelistItem {
  param: string;
  method: string;
}

const whitelistWithMethod: WhitelistItem[] = [
  { param: '_configuration', method: 'setPayoutMods' },
  { param: '_configuration', method: 'setTicketMods' },
];

export enum TIMESTAMP_LIKELIHOOD {
  LIKELY = 'Likely',
  POSSIBLE = 'Possible',
  UNLIKELY = 'Unlikely',
}

/**
 * Determine if a numeric value is likely a Unix epoch timestamp (in seconds)
 * based on the value itself and contextual hints like parameter/method names.
 *
 * @param value - The numeric value to check
 * @param paramName - The name of the parameter (optional)
 * @param methodName - The name of the method (optional, rarely used)
 * @returns TIMESTAMP_LIKELIHOOD classification
 */
export function isLikelyAnEpochTime(
  value: number,
  paramName: string | undefined,
  methodName?: string // optional bc it's rare to match on this and we don't always have it at the time
): TIMESTAMP_LIKELIHOOD {
  // Case: param name is undefined or empty string - not enough info to assume it's a timestamp
  if (!paramName) {
    return TIMESTAMP_LIKELIHOOD.UNLIKELY;
  }

  // Case: param name contains one of the blacklisted strings
  if (blacklist.some((str: string) => paramName.toLowerCase().includes(str))) {
    return TIMESTAMP_LIKELIHOOD.UNLIKELY;
  }

  // Case: value is too early or late to be a valid timestamp
  const threeYearsAfterToday = Date.now() + threeYears; // Date.now() is current unix epoch milliseconds
  if (
    value < ethereumLaunchDate / 1000 ||
    value > threeYearsAfterToday / 1000
  ) {
    return TIMESTAMP_LIKELIHOOD.UNLIKELY;
  }

  const isTimestampParamName: boolean = whitelist.some((str: string) =>
    paramName.toLowerCase().includes(str)
  );
  const isTimestampParamAndMethodName: boolean = whitelistWithMethod.some(
    (item: WhitelistItem) =>
      item.param === paramName && item.method === methodName
  );

  // Case: timestamp is in a reasonable range and the param name indicates it's a timestamp
  if (isTimestampParamName || isTimestampParamAndMethodName) {
    return TIMESTAMP_LIKELIHOOD.LIKELY;
  } else {
    return TIMESTAMP_LIKELIHOOD.POSSIBLE;
  }
}
